import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Globe, Plus, X, Loader2, Sparkles, Store, Truck, ShoppingBag, ExternalLink, TrendingUp, AlertTriangle, Play, Pencil, Check, Layers } from "lucide-react";

const TYPE_META = {
  perakende: { label: "Perakende", icon: Store, cls: "text-brand-orange" },
  toptan: { label: "Toptan", icon: Truck, cls: "text-sky-600" },
  karma: { label: "Karma", icon: ShoppingBag, cls: "text-slate-500" },
};

const TypeChip = ({ type }) => {
  const m = TYPE_META[type] || TYPE_META.karma;
  const Icon = m.icon;
  return <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${m.cls}`}><Icon className="h-3 w-3" /> {m.label}</span>;
};

const EMPTY = { name: "", base: "", type: "karma", editing: null };

export default function MarketSourcesSettings() {
  const { can, activeSectorObj } = useAuth();
  const editable = can("price.write");
  const sectorLabel = activeSectorObj?.name || "Tüm Sektörler";
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sources, setSources] = useState([]);
  const [nf, setNf] = useState(EMPTY);
  const [aiQuery, setAiQuery] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [wMethod, setWMethod] = useState("scraper");
  const [savingW, setSavingW] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [alertCfg, setAlertCfg] = useState({ enabled: false, direction: "both", min_dev: 10, email: false });
  const [savingAlert, setSavingAlert] = useState(false);

  const loadAuto = async () => {
    try {
      const [s, w, a] = await Promise.all([
        api.get("/market-research/summary").catch(() => ({ data: null })),
        api.get("/market/weekly-research/config").catch(() => ({ data: null })),
        api.get("/settings/market-alert-notify").catch(() => ({ data: null })),
      ]);
      if (s.data) setSummary(s.data);
      if (w.data?.method) setWMethod(w.data.method);
      if (a.data) setAlertCfg({ enabled: !!a.data.enabled, direction: a.data.direction || "both", min_dev: a.data.min_dev ?? 10, email: !!a.data.email });
    } catch { /* yetki yoksa sessiz */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/market-sources");
      setSources(data.sources || []);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  // Aktif sektör değişince o sektörün listesini yükle.
  useEffect(() => { load(); loadAuto(); }, [activeSectorObj?.code]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeCount = sources.filter((s) => s.enabled !== false).length;

  const persist = async (next) => {
    setSources(next);
    setBusy(true);
    try {
      const { data } = await api.put("/settings/market-sources", {
        sources: next.map(({ name, base, type, enabled }) => ({ name, base, type, enabled: enabled !== false })),
      });
      toast.success(`Kaydedildi · ${data.active_total} aktif kaynak (${sectorLabel})`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
    finally { setBusy(false); }
  };

  const exists = (name, exceptName) => sources.some((x) => x.name.toLowerCase() === name.toLowerCase() && x.name !== exceptName);

  const addOrUpdate = (site) => {
    if (!editable) return;
    const name = (site?.name ?? nf.name).trim();
    const base = (site?.base ?? nf.base).trim();
    const type = site?.type ?? nf.type;
    if (!name || !base) { toast.error("Ad ve site adresi gerekli"); return; }
    if (exists(name, site ? null : nf.editing)) { toast.error("Bu isimde kaynak zaten var"); return; }
    let next;
    if (!site && nf.editing) {
      next = sources.map((s) => (s.name === nf.editing ? { name, base, type, enabled: s.enabled !== false } : s));
    } else {
      next = [...sources, { name, base, type, enabled: true }];
    }
    setNf(EMPTY);
    setSuggestions((s) => s.filter((x) => x.name.toLowerCase() !== name.toLowerCase()));
    persist(next);
  };

  const remove = (name) => { if (editable) persist(sources.filter((x) => x.name !== name)); };
  const toggle = (name) => { if (editable) persist(sources.map((s) => (s.name === name ? { ...s, enabled: !(s.enabled !== false) } : s))); };
  const startEdit = (s) => { if (editable) setNf({ name: s.name, base: s.base, type: s.type || "karma", editing: s.name }); };

  const aiSuggest = async () => {
    if (!aiQuery.trim()) { toast.error("Ürün/hizmet veya kategori girin"); return; }
    setAiBusy(true); setSuggestions([]);
    try {
      const { data } = await api.post("/settings/market-sources/ai-suggest", { query: aiQuery.trim() });
      setSuggestions(data.sites || []);
      if (!(data.sites || []).length) toast.info("AI öneri bulamadı, farklı bir ifade deneyin");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "AI öneri veremedi"); }
    finally { setAiBusy(false); }
  };

  const saveWeekly = async (m) => {
    setWMethod(m); setSavingW(true);
    try { await api.put("/market/weekly-research/config", { method: m }); toast.success(m === "ai" ? "Otomatik tarama: Google AI" : "Otomatik tarama: Site Tarama"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingW(false); }
  };

  const runScan = async () => {
    setScanBusy(true);
    try {
      const { data } = await api.post("/products/market-price/run", { method: wMethod, scope: "all", limit: 300 });
      if (data.started > 0) toast.success(data.message);
      else toast.info(data.message || "Taranacak ürün yok");
      setTimeout(loadAuto, 1500);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setScanBusy(false); }
  };

  const saveAlert = async (patch) => {
    const next = { ...alertCfg, ...patch, min_dev: Number((patch.min_dev ?? alertCfg.min_dev)) || 10 };
    setAlertCfg(next); setSavingAlert(true);
    try { await api.put("/settings/market-alert-notify", next); toast.success("Rakip fiyat alarmı ayarı kaydedildi"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingAlert(false); }
  };

  return (
    <div data-testid="market-sources-settings" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
          <Globe className="h-5 w-5 text-brand-green" /> Piyasa Araştırma Kaynakları
        </p>
        <span data-testid="ms-sector-badge" className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-slate-600 ring-1 ring-inset ring-slate-200">
          <Layers className="h-3 w-3" /> {sectorLabel}
        </span>
        <span data-testid="ms-active-count" className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-bold text-emerald-700 ring-1 ring-inset ring-emerald-200">
          {activeCount}/{sources.length} aktif site
        </span>
        {busy && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>
      <p className="-mt-1 text-sm text-slate-500">Bu liste <b>seçili sektöre</b> ({sectorLabel}) özeldir — her sektör kendi kaynaklarını tutar. Site ekleyin, düzenleyin, silin veya taramadan geçici çıkarın. Değişiklikler <b>anında</b> kaydedilir.</p>

      {/* AI Otomatik Tarama & Sapma Uyarıları */}
      <div data-testid="ms-autoscan-card" className="rounded-xl border border-sky-200 bg-sky-50/50 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <p className="flex items-center gap-2 font-heading text-sm font-bold text-slate-800">
            <TrendingUp className="h-4 w-4 text-sky-600" /> AI Otomatik Tarama & Sapma Uyarıları
          </p>
          <span className="ml-auto flex items-center gap-1 text-[11px] text-slate-500">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
            <b data-testid="ms-alert-count" className="text-amber-600">{summary?.alerts ?? 0}</b> sapma uyarısı
          </span>
        </div>
        <p className="mb-2.5 mt-0.5 text-[11px] text-slate-500">
          Yukarıdaki siteler kullanılarak fiyatlar taranır; piyasa ortalaması alış fiyatınızdan <b>≥%10</b> saparsa ürün kartında otomatik uyarı çıkar.
          Aktif kullanıcı yokken haftalık otomatik da çalışır.
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <div className="rounded-lg bg-white p-2 text-center ring-1 ring-inset ring-slate-200">
            <p className="text-[10px] text-slate-400">Takip Edilen</p>
            <p className="tabular text-base font-bold text-slate-800" data-testid="ms-tracked">{summary?.tracked ?? "—"}</p>
          </div>
          <div className="rounded-lg bg-white p-2 text-center ring-1 ring-inset ring-slate-200">
            <p className="text-[10px] text-slate-400">Sapma Uyarısı</p>
            <p className="tabular text-base font-bold text-amber-600">{summary?.alerts ?? "—"}</p>
          </div>
          <div className="rounded-lg bg-white p-2 text-center ring-1 ring-inset ring-slate-200">
            <p className="text-[10px] text-slate-400">Piyasa Üstü</p>
            <p className="tabular text-base font-bold text-red-500">{summary?.above ?? "—"}</p>
          </div>
          <div className="rounded-lg bg-white p-2 text-center ring-1 ring-inset ring-slate-200">
            <p className="text-[10px] text-slate-400">Piyasa Altı</p>
            <p className="tabular text-base font-bold text-emerald-600">{summary?.below ?? "—"}</p>
          </div>
        </div>
        {editable && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold text-slate-500">Tarama yöntemi:</span>
            <div className="inline-flex rounded-md bg-white p-0.5 ring-1 ring-inset ring-slate-200" data-testid="ms-method-toggle">
              {[["scraper", "Site Tarama (ücretsiz)"], ["ai", "Google AI (güvenilir)"]].map(([k, l]) => (
                <button key={k} data-testid={`ms-method-${k}`} onClick={() => saveWeekly(k)} disabled={savingW}
                  className={`rounded-sm px-2.5 py-1 text-[11px] font-semibold transition-colors ${wMethod === k ? "bg-sky-600 text-white" : "text-slate-500 hover:text-slate-700"}`}>{l}</button>
              ))}
            </div>
            <Button data-testid="ms-run-scan" onClick={runScan} disabled={scanBusy} size="sm" className="ml-auto h-8 bg-sky-600 text-white hover:bg-sky-700">
              {scanBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-1.5 h-3.5 w-3.5" />} Şimdi Tara
            </Button>
          </div>
        )}
        {editable && (
          <div className="mt-3 rounded-lg bg-white p-3 ring-1 ring-inset ring-slate-200" data-testid="ms-alert-config">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                <input type="checkbox" data-testid="ms-alert-enabled" checked={alertCfg.enabled} onChange={(e) => saveAlert({ enabled: e.target.checked })} className="h-4 w-4 accent-sky-600" />
                Rakip Fiyat Alarmı
              </label>
              <span className="text-[11px] text-slate-400">Satış fiyatı piyasadan saparsa anlık bildirim/e-posta.</span>
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <select data-testid="ms-alert-direction" value={alertCfg.direction} onChange={(e) => saveAlert({ direction: e.target.value })} disabled={!alertCfg.enabled}
                  className="h-8 rounded-md border border-slate-300 bg-white px-2 text-[11px] disabled:opacity-50">
                  <option value="both">Her iki yön</option>
                  <option value="above">Sadece piyasa üstü</option>
                  <option value="below">Sadece piyasa altı</option>
                </select>
                <label className="flex items-center gap-1 text-[11px] text-slate-500">≥%
                  <Input data-testid="ms-alert-mindev" type="number" min="1" max="90" value={alertCfg.min_dev}
                    onChange={(e) => setAlertCfg({ ...alertCfg, min_dev: e.target.value })}
                    onBlur={() => saveAlert({ min_dev: Number(alertCfg.min_dev) || 10 })} disabled={!alertCfg.enabled} className="h-8 w-16" />
                </label>
                <label className="flex items-center gap-1 text-[11px] text-slate-600">
                  <input type="checkbox" data-testid="ms-alert-email" checked={alertCfg.email} onChange={(e) => saveAlert({ email: e.target.checked })} disabled={!alertCfg.enabled} className="h-4 w-4 accent-sky-600" />
                  E-posta
                </label>
                {savingAlert && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
              </div>
            </div>
          </div>
        )}
        {summary?.last_refresh && <p className="mt-2 text-[10px] text-slate-400">Son tarama: {String(summary.last_refresh).slice(0, 16).replace("T", " ")}</p>}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Sol: AI öneri + ekle/düzenle formu */}
          <div className="space-y-4">
            <div data-testid="ms-ai-card" className="rounded-xl border border-brand-green/30 bg-emerald-50/40 p-4">
              <p className="flex items-center gap-2 font-heading text-sm font-bold text-slate-800">
                <Sparkles className="h-4 w-4 text-brand-green" /> AI ile Site Öner
              </p>
              <p className="mb-2.5 mt-0.5 text-[11px] text-slate-500">İlgili ürün/hizmet ya da kategori için AI, fiyatın halka açık listelendiği gerçek Türk sitelerini önerir.</p>
              <div className="flex gap-2">
                <Input data-testid="ms-ai-query" value={aiQuery} onChange={(e) => setAiQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); aiSuggest(); } }}
                  placeholder="örn: zeytinyağı, temizlik ürünleri, oto yedek parça" className="h-9" disabled={!editable} />
                <Button data-testid="ms-ai-suggest" onClick={aiSuggest} disabled={aiBusy || !editable}
                  className="h-9 shrink-0 bg-brand-green text-white hover:bg-brand-green-600">
                  {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  <span className="ml-1.5 hidden sm:inline">Öner</span>
                </Button>
              </div>
              {suggestions.length > 0 && (
                <div data-testid="ms-ai-suggestions" className="mt-3 max-h-52 space-y-1.5 overflow-y-auto pr-1">
                  {suggestions.map((s, i) => (
                    <div key={i} data-testid={`ms-suggestion-${i}`} className="flex items-center gap-2 rounded-lg bg-white px-2.5 py-2 ring-1 ring-inset ring-slate-200">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-xs font-semibold text-slate-800">{s.name}</span>
                          <TypeChip type={s.type} />
                        </div>
                        {s.why && <p className="truncate text-[10px] text-slate-400">{s.why}</p>}
                      </div>
                      <a href={s.base} target="_blank" rel="noreferrer" className="shrink-0 text-slate-300 hover:text-brand-green"><ExternalLink className="h-3.5 w-3.5" /></a>
                      <Button data-testid={`ms-suggestion-add-${i}`} onClick={() => addOrUpdate(s)} disabled={exists(s.name) || !editable}
                        size="sm" variant="outline" className="h-7 shrink-0 px-2 text-[11px]">
                        {exists(s.name) ? "Ekli" : <><Plus className="mr-0.5 h-3 w-3" /> Ekle</>}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="flex items-center gap-2 font-heading text-sm font-bold text-slate-800">
                {nf.editing ? <Pencil className="h-4 w-4 text-sky-600" /> : <Plus className="h-4 w-4 text-brand-green" />}
                {nf.editing ? `Kaynağı Düzenle: ${nf.editing}` : "Yeni Kaynak Ekle"}
              </p>
              {editable && (
                <div className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto]">
                  <Input data-testid="ms-new-name" value={nf.name} onChange={(e) => setNf((n) => ({ ...n, name: e.target.value }))} placeholder="Site adı" className="h-9" />
                  <Input data-testid="ms-new-base" value={nf.base} onChange={(e) => setNf((n) => ({ ...n, base: e.target.value }))} placeholder="alanadi.com" className="h-9" />
                  <div className="flex gap-2">
                    <select data-testid="ms-new-type" value={nf.type} onChange={(e) => setNf((n) => ({ ...n, type: e.target.value }))}
                      className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm">
                      <option value="karma">Karma</option>
                      <option value="perakende">Perakende</option>
                      <option value="toptan">Toptan</option>
                    </select>
                    <Button data-testid="ms-save-source" onClick={() => addOrUpdate()} className="h-9 bg-brand-green text-white hover:bg-brand-green-600">
                      {nf.editing ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                    </Button>
                    {nf.editing && <Button data-testid="ms-cancel-edit" onClick={() => setNf(EMPTY)} variant="outline" className="h-9 px-2"><X className="h-4 w-4" /></Button>}
                  </div>
                </div>
              )}
              {!editable && <p className="mt-2 text-xs text-slate-400">Düzenlemek için fiyat yazma yetkisi gerekir.</p>}
            </div>
          </div>

          {/* Sağ: sektörün TÜM kaynakları (düzenle/sil/aç-kapa) */}
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="flex items-center gap-2 font-heading text-sm font-bold text-slate-800">
              <Globe className="h-4 w-4 text-brand-green" /> Kaynak Listesi
              <span className="ml-auto text-[11px] font-normal text-slate-400">{activeCount}/{sources.length} açık</span>
            </p>
            <p className="mb-2.5 mt-0.5 text-[11px] text-slate-400">Kapalı siteler taranmaz. Kalem ikonuyla düzenleyin, çöp ikonuyla tümüyle silin.</p>
            <div className="max-h-[52vh] space-y-1.5 overflow-y-auto pr-1" data-testid="ms-source-list">
              {sources.length === 0 && <p className="py-6 text-center text-sm text-slate-400">Bu sektörde kaynak yok. Soldan ekleyin.</p>}
              {sources.map((b) => (
                <div key={b.name} data-testid={`ms-source-${b.name}`}
                  className={`flex items-center gap-2 rounded-lg px-2.5 py-2 ring-1 ring-inset transition-colors ${b.enabled !== false ? "bg-emerald-50/60 ring-emerald-200" : "bg-slate-50 ring-slate-200 opacity-70"}`}>
                  <input type="checkbox" data-testid={`ms-toggle-${b.name}`} checked={b.enabled !== false} disabled={!editable}
                    onChange={() => toggle(b.name)} className="h-4 w-4 accent-emerald-600" title={b.enabled !== false ? "Taramada açık" : "Kapalı"} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2"><span className="truncate text-xs font-semibold text-slate-800">{b.name}</span><TypeChip type={b.type} /></div>
                    <p className="truncate text-[10px] text-slate-400">{b.base}</p>
                  </div>
                  <a href={b.base?.startsWith("http") ? b.base : `https://${b.base}`} target="_blank" rel="noreferrer" className="shrink-0 text-slate-300 hover:text-brand-green" title="Siteyi aç"><ExternalLink className="h-3.5 w-3.5" /></a>
                  {editable && (
                    <>
                      <button type="button" data-testid={`ms-edit-${b.name}`} onClick={() => startEdit(b)} title="Düzenle"
                        className="shrink-0 rounded-full p-1 text-slate-400 transition-colors hover:bg-sky-50 hover:text-sky-600"><Pencil className="h-3.5 w-3.5" /></button>
                      <button type="button" data-testid={`ms-del-${b.name}`} onClick={() => remove(b.name)} title="Sil"
                        className="shrink-0 rounded-full p-1 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"><X className="h-4 w-4" /></button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
